import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  Linkedin,
  Download,
  ArrowRight,
  CheckCircle,
  Building2,
  Activity,
  Cpu,
  Network,
  Gauge,
  BarChart3,
  Award,
  ShieldCheck,
  ServerCog,
  Layers,
  Database,
  Bot,
} from "lucide-react";

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const year = new Date().getFullYear();

  const profile = {
    name: "Bishar Basheer",
    title:
      "Senior Healthcare Integration Architect | HL7, Rhapsody, Cerner PathNet & FSI Expert | Power BI Dashboard Builder | POCT & Automation Leader",
    tagline:
      "18+ years delivering enterprise-grade, clinical integrations across national and international healthcare ecosystems.",
    summary:
      "I specialize in Cerner Millennium (PathNet), FSI, HL7, Rhapsody, and Mirth—connecting siloed systems into unified, safe, and scalable clinical workflows.",
    email: "bnbishar@gmail.com",
    phone: "+971 55 695 1445",
    location: "Abu Dhabi, UAE",
    linkedin: "https://linkedin.com/in/bisharbn",
  };

  const coreCompetencies = [
    { icon: Network, label: "Integration Engines", items: ["HL7", "Rhapsody", "Mirth", "Ensemble", "Iguana", "OpenLink"] },
    { icon: Building2, label: "Cerner Expertise", items: ["PathNet", "FSI", "MDI", "Microbiology", "AP", "Billing", "PACS", "Vocera"] },
    { icon: BarChart3, label: "Power BI", items: ["Lab Ops", "TAT", "Vocera Utilization", "Interface Status"] },
    { icon: Gauge, label: "POCT Mgmt", items: ["1000+ devices", "260+ facilities", "Governance", "QC"] },
    { icon: Activity, label: "National Platforms", items: ["Malaffi", "HASANA", "NADBIDH", "Riayati", "Al HOSN", "IATA"] },
    { icon: Layers, label: "ERP", items: ["Oracle Financials", "Activity-Based Costing"] },
    { icon: Mail, label: "Patient Tools", items: ["SMS Alerts", "QR-coded Reports", "Outreach Portals"] },
    { icon: Bot, label: "Automation & AI", items: ["UiPath RPA", "Auto-Recovery", "GenAI Integration"] },
  ];

  const certifications = [
    "HL7 Certified",
    "Rhapsody Professional Certified",
    "Radiometer Training Certificate (2025)",
    "Six Sigma Green Belt",
    "UiPath RPA Developer",
    "Full Stack Developer – Python & .NET",
  ];

  const flagship = [
    {
      title: "Referral Lab Integration",
      detail:
        "Routed orders from 100+ SEHA clinics/hospitals to PureLab (PureHealth) via Cerner FSI with zero disruption to live workflows.",
    },
    {
      title: "Referral Lab Dashboard",
      detail:
        "Power BI dashboard tracking order volumes, TAT, and top referrers to proactively manage lab operations.",
    },
    {
      title: "Vocera Integration Dashboard",
      detail: "Mirth + SQL + Power BI to monitor physician voice comms and lab engagement in real time.",
    },
    {
      title: "Cerner Interface Monitoring Bot",
      detail:
        "Automated COM server failure alerts via email to fix issues before patient care is affected.",
    },
    {
      title: "POCT QC Framework",
      detail:
        "Built internal governance comparing Siggaard-Andersen, Courineau, Fousé, Kratzer-Courineau models across SEHA POCT network.",
    },
    {
      title: "CCAD & Mubadala Integration Architecture",
      detail:
        "Complete roadmap for Cerner, Omnicell, Digital Pathology, external billing, and EMR systems.",
    },
  ];

  const projects = [
    {
      name: "HL7 Integration Programs",
      bullets: [
        "End-to-end integrations for 100+ clients (incl. CCAD, DHA, Capital Health)",
        "ORM/ORU design, implementation, go-live, and post-deployment support",
      ],
      icon: ServerCog,
    },
    {
      name: "NRL Healthcheckup Utilities",
      bullets: [
        "In-house utilities integrated with Cerner, ManageEngine, MyCare Billing, Kofax, Oracle DB",
        "Automated HL7 test engine for bulk service validation",
      ],
      icon: Cpu,
    },
    {
      name: "MyCare Billing Solutions",
      bullets: [
        "Functional design, workflows, and reconciliations to improve invoice accuracy",
        "VAT feasibility, analysis, execution, validation, and closure",
      ],
      icon: Database,
    },
    {
      name: "BI Dashboards (In-house)",
      bullets: [
        "Web-based org dashboards across operational, clinical, and financial KPIs",
        "Data quality stewardship and source accuracy governance",
      ],
      icon: BarChart3,
    },
  ];

  const experience = [
    {
      org: "Sheikh Shakhbout Medical City (SSMC), Abu Dhabi",
      role: "Senior Clinical Analyst / Lead / BRM for Lab Operations",
      dates: "Apr 2023 – Present",
      highlights: [
        "Digital Pathology and Vocera Integration",
        "Microbiology Lab expansion and automation",
        "Support for PowerChart, RCMS, FirstNet, SurgiNet",
        "Training programs, SOPs, and technical documentation",
        "Manage 1,000+ POCT devices across 260+ SEHA facilities",
        "Architectural roadmaps aligned to SEHA digital strategy",
      ],
    },
    {
      org: "National Reference Laboratory (Mubadala Health), Abu Dhabi",
      role: "Senior System Analyst / Acting Integration Manager",
      dates: "Apr 2015 – Apr 2023",
      highlights: [
        "Acting Integration Manager & Architect for Mubadala Health",
        "FSI/Mirth development for message filtering, routing, transformation",
        "Health-check reporting and proactive monitoring (in-house tools)",
        "Malaffi integration for ADT, SIU, ORM/OML/ORR/ORU, VXU, AP, Micro",
      ],
    },
    {
      org: "Health Care Solution, Dubai",
      role: "Application Consultant / Senior Software Engineer",
      dates: "Mar 2012 – Apr 2015",
      highlights: [
        "Utilities for HL7 & ASTM device communication",
        "PDMS for Dubai Health Authority",
        "HIS modules: OT, appointment, registration",
      ],
    },
  ];

  const section = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const chip = (text) => (
    <span className="inline-flex items-center rounded-full border border-sky-300/70 bg-sky-50 px-3 py-1 text-xs text-sky-800">
      {text}
    </span>
  );
  const phoneHref = useMemo(() => `tel:${profile.phone.replace(/\s|\+/g, "")}`, [profile.phone]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMenuOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 text-slate-800">
      <header className="sticky top-0 z-50 border-b border-sky-100/80 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-600 text-white shadow-sm">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-sky-700">{profile.name}</p>
              <p className="text-xs text-slate-500">Healthcare Integration Architect</p>
            </div>
          </div>
          <nav className="hidden items-center gap-6 text-sm lg:flex">
            {[
              ["About", "about"],
              ["Skills", "skills"],
              ["Certifications", "certs"],
              ["Highlights", "highlights"],
              ["Projects", "projects"],
              ["Experience", "experience"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <button key={id} onClick={() => section(id)} className="text-slate-600 transition hover:text-sky-700">
                {label}
              </button>
            ))}
          </nav>
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800 hover:bg-sky-100 lg:hidden"
            onClick={() => setMenuOpen((v) => !v)} aria-label="Toggle menu">Menu</button>
        </div>
        {menuOpen && (
          <div className="border-t border-sky-100 bg-white px-4 py-3 lg:hidden">
            <div className="grid grid-cols-2 gap-2">
              {["about","skills","certs","highlights","projects","experience","contact"].map((id) => (
                <button key={id} onClick={() => { section(id); setMenuOpen(false); }}
                  className="rounded-lg bg-sky-50 px-3 py-2 text-left text-sm text-sky-800 hover:bg-sky-100">
                  {id[0].toUpperCase()+id.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <section className="mx-auto max-w-7xl px-4 pt-10 sm:pt-14">
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-6">
            <h1 className="text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">{profile.title}</h1>
            <p className="text-base text-slate-600">{profile.tagline} {profile.summary}</p>
            <div className="flex flex-wrap items-center gap-3">
              {/* <a href="/Bishar_Basheer_CV.pdf"
                 className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-white shadow hover:bg-sky-700">
                <Download className="h-4 w-4" /> Download Resume
              </a> */}
              <a href={`mailto:${profile.email}`}
                 className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-white px-4 py-2 text-sky-800 hover:bg-sky-50">
                <Mail className="h-4 w-4" /> Email
              </a>
              <a href={profile.linkedin} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-white px-4 py-2 text-sky-800 hover:bg-sky-50">
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
              <a href={phoneHref}
                 className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-white px-4 py-2 text-sky-800 hover:bg-sky-50">
                <Phone className="h-4 w-4" /> Call
              </a>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {chip(profile.location)}
              {chip("18+ years experience")}
              {chip("Available for consulting")}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-3xl border border-sky-100 bg-white/70 p-6 shadow-sm">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {[["100+","Lab Integrations"],["1,000+","POCT Devices"],["260+","SEHA Facilities"],["6+","Major Platforms"],["24/7","Ops Reliability"],["0","Go-Live Disruptions"]]
                .map(([stat, label]) => (
                <div key={label} className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4 text-center">
                  <p className="text-xl font-bold text-sky-700">{stat}</p>
                  <p className="text-xs text-slate-600">{label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <main className="mx-auto mt-10 max-w-7xl gap-8 px-4 lg:mt-14 lg:grid lg:grid-cols-3">
        <div className="lg:col-span-2 lg:space-y-8">
          <section id="skills" className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-sky-600" />
              <h2 className="text-xl font-semibold">Core Competencies</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {coreCompetencies.map((c) => (
                <div key={c.label} className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sky-800">
                    <c.icon className="h-4 w-4" />
                    <p className="font-medium">{c.label}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.items.map((i) => chip(i))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="certs" className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Award className="h-5 w-5 text-sky-600" />
              <h2 className="text-xl font-semibold">Certifications</h2>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {certifications.map((c) => (
                <li key={c} className="flex items-start gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-sky-600" />
                  <span className="text-sm text-slate-700">{c}</span>
                </li>
              ))}
            </ul>
          </section>

          <section id="highlights" className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Gauge className="h-5 w-5 text-sky-600" />
              <h2 className="text-xl font-semibold">Flagship Achievements</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {flagship.map((f) => (
                <div key={f.title} className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
                  <p className="font-medium text-sky-800">{f.title}</p>
                  <p className="mt-1 text-sm text-slate-700">{f.detail}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="projects" className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <ServerCog className="h-5 w-5 text-sky-600" />
              <h2 className="text-xl font-semibold">Key Projects Managed</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {projects.map((p) => (
                <div key={p.name} className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sky-800">
                    <p.icon className="h-4 w-4" />
                    <p className="font-medium">{p.name}</p>
                  </div>
                  <ul className="list-inside list-disc text-sm text-slate-700">
                    {p.bullets.map((b, idx) => (<li key={idx}>{b}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section id="experience" className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-sky-600" />
              <h2 className="text-xl font-semibold">Work Experience</h2>
            </div>
            <div className="space-y-6">
              {experience.map((e) => (
                <div key={e.org} className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
                  <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                    <p className="font-semibold text-sky-800">{e.org}</p>
                    <p className="text-sm text-slate-600">{e.dates}</p>
                  </div>
                  <p className="text-sm text-slate-700">{e.role}</p>
                  <ul className="mt-2 grid list-inside list-disc gap-1 text-sm text-slate-700 sm:grid-cols-2">
                    {e.highlights.map((h, idx) => (<li key={idx}>{h}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="sticky top-24 space-y-8 self-start">
          <section id="about" className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <h2 className="mb-2 text-xl font-semibold">About</h2>
            <p className="text-sm text-slate-700">
              I turn integration complexity into clinical clarity—securely, efficiently, and at scale. From routing orders across SEHA to
              building dashboards for TAT and clinician alerts, to integrating 1,000+ POCT devices and national platforms like Malaffi and Riayati.
            </p>
          </section>

          <section id="contact" className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-xl font-semibold">Contact</h2>
            <div className="space-y-3 text-sm">
              <a href={`mailto:${profile.email}`} className="group flex items-center gap-2 text-slate-700 hover:text-sky-700">
                <Mail className="h-4 w-4" /> {profile.email}
                <ArrowRight className="h-4 w-4 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
              </a>
              <a href={phoneHref} className="group flex items-center gap-2 text-slate-700 hover:text-sky-700">
                <Phone className="h-4 w-4" /> {profile.phone}
                <ArrowRight className="h-4 w-4 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
              </a>
              <a href={profile.linkedin} target="_blank" rel="noreferrer" className="group flex items-center gap-2 text-slate-700 hover:text-sky-700">
                <Linkedin className="h-4 w-4" /> linkedin.com/in/bisharbn
                <ArrowRight className="h-4 w-4 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
              </a>
              <p className="text-slate-500">{profile.location}</p>
            </div>
            {/* <div className="mt-4">
              <button onClick={() => window.print()}
                className="w-full rounded-xl bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-sky-700">
                Print/Save as PDF
              </button>
            </div> */}
          </section>

          <section className="rounded-3xl border border-sky-100 bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-xl font-semibold">Quick Facts</h2>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• Complete Integration Architect design for Mubadala & CCAD</li>
              <li>• Oracle ERP Financials & Activity-Based Costing</li>
              <li>• Outreach portals, SMS, QR on Cerner reports</li>
              <li>• RPA bot for Cerner error auto-recovery</li>
              <li>• CCL & BI dashboards for leaders and end users</li>
              <li>• ASTM/HL7 medical device integrations</li>
            </ul>
          </section>
        </aside>
      </main>

      <footer className="mt-12 border-t border-sky-100/70 bg-white/60">
        <div className="mx-auto max-w-7xl px-4 py-6 text-xs text-slate-500">
          © {year} {profile.name}. All rights reserved.
        </div>
      </footer>

      <style>{`@media print {
        header, footer, button, nav { display: none !important; }
        a { text-decoration: none; color: inherit; }
        section { break-inside: avoid; }
        body { background: white; }
      }`}</style>
    </div>
  );
}
